// Panel countries that get the full field set on child tabs
export const PANEL_COUNTRIES = ['IN', 'MY', 'SG', 'UK', 'PH', 'JP'];

// Full country names for panel countries (used in Details modal and list table)
export const PANEL_COUNTRY_NAMES = ['India', 'Malaysia', 'Singapore', 'United Kingdom', 'Philippines', 'Japan'];

export const isPanelCountry = (shortName: string): boolean =>
  PANEL_COUNTRIES.includes(shortName?.toUpperCase());

export const isPanelCountryByName = (countryName: string): boolean =>
  PANEL_COUNTRY_NAMES.includes(countryName);

// Fields that belong to the parent project only (not editable per-child)
export const PARENT_ONLY_FIELDS = [
  'projectName', 'projectId', 'description', 'pmName', 'client',
  'clientir', 'costPerInterview', 'currency', 'publishDate', 'expiryDate',
  'surveyLength', 'outlierCutoffTime', 'validateStartEndIp', 'validateCountry',
  'blacklistSurvey', 'surveyUrlIdentifier', 'colorCode',
  'description_one', 'description_two', 'description_three', 'description_four',
];

// Fields that can be overridden per-child (panel countries)
// 'preQuestionsYN' removed — pre-screening is now fully owned per-country, so
// override-tracking against a parent flag is no longer meaningful.
export const CHILD_OVERRIDABLE_FIELDS = [
  'name', 'ceggPoints', 'overquota', 'terminate', 'qualityterminate',
  'userLimitCommitted', 'userLimitCutoff', 'IPollsQuotaLimit',
  'pointAllocationType', 'surveyType',
  'whatsapp', 'email', 'sms',
  'useUniqueLinks', 'verisoul', 'verisoul_risk_threshold', 'url', 'testurl', 'disclaimer',
];

// Fields available for non-panel country children (minimal set)
export const NON_PANEL_CHILD_FIELDS = [
  'useUniqueLinks', 'url', 'testurl',
];

export const SURVEY_DESCRIPTION_OPTIONS = [
  'Education', 'Fashion and Accessories', 'Financial Services', 'Food',
  'Healthcare', 'Gadgets', 'Home Care', 'Lifestyles and Hobbies',
  'Media and Entertainment', 'Corporate Related', 'Personal Care and Beauty',
  'Pet Care', 'Shopping and Retail', 'Technology and Computing',
  'Toys and Games', 'Travel', 'Exciting New', 'Augment Survey',
];

// A new panel market's launch settings (SCRUM-34): Point Allocation Auto,
// Send Survey by Email only. Saved markets keep their stored values; external
// markets don't use these fields (Manual / N).
export const NEW_PANEL_MARKET_DEFAULTS = {
  pointAllocationType: 'Auto',
  email: 'Y',
  sms: 'N',
  whatsapp: 'N',
};

// The status of a market added to an existing survey (SCRUM-34): On Hold (the
// paused status), never the project's possibly Live one. It goes Live from
// Overview once its links are in. Same value as the admin backend's.
export const NEW_MARKET_STATUS = 'Hold';

// Default values for a new child survey entry
export const DEFAULT_CHILD_STATE = {
  name: '',
  // Free-form, user-editable per-child label (DB column surveys.childProjectName).
  // Distinct from `name` (SMS/WhatsApp name) and `projectname` (the deterministic
  // `${parent}_${shortName}_N` Stage-1 value). Replaces the old computed, read-only
  // "Child Project ID" display. Nullable; empty by default and for duplicates.
  childProjectName: '',
  ceggPoints: 0,
  overquota: 0,
  terminate: 0,
  qualityterminate: 0,
  userLimitCommitted: 0,
  userLimitCutoff: 0,
  IPollsQuotaLimit: 'N',
  pointAllocationType: NEW_PANEL_MARKET_DEFAULTS.pointAllocationType,
  surveyType: 'Test',
  whatsapp: NEW_PANEL_MARKET_DEFAULTS.whatsapp,
  email: NEW_PANEL_MARKET_DEFAULTS.email,
  sms: NEW_PANEL_MARKET_DEFAULTS.sms,
  useUniqueLinks: false,
  // Which pool an uploaded Unique Links CSV targets. 'panelist' (default) feeds
  // the DX panelist pool; 'partner' feeds the attached partner's pool instead.
  // Only meaningful while useUniqueLinks is true — see ChildSurveyTab's radio pair.
  poolTarget: 'panelist' as 'panelist' | 'partner',
  verisoul: false,
  verisoul_risk_threshold: 70,
  url: '',
  testurl: '',
  disclaimer: '',
  overriddenFields: [] as string[],
  // Pre-screening, per-country (own toggle, own questions, own qualifying answers).
  // 'Y' = enabled for this market, 'N' (or null) = disabled.
  preQuestionsYN: 'N' as string | null,
  // Picked questions for this market (option shape from preScreenOptions).
  selectedPreScreenQues: [] as any[],
  // Canonical persistence shape: { id, question, questionType, type, answerId }.
  details: [] as any[],
  // Question IDs that have an answer mapping saved (drives the Update vs Select button).
  submittedAnswerDets: [] as string[],
  // Legacy field — kept on the wire as [] for back-compat with backend filters
  // that read it; with per-country picking it is no longer meaningful.
  removedPreScreenQuestionIds: [] as string[],
};

export type ChildSurveyState = typeof DEFAULT_CHILD_STATE & {
  id?: string;         // existing child survey ID (edit mode)
  createdAt?: any;     // used to sort same-market duplicates ("AU", "AU 2", ...)
  country: string;     // country name
  shortName: string;   // country short code
};

export type ParentState = {
  projectName: string;
  projectId: string;
  description: string;
  pmName: string;
  client: any;
  clientir: string;
  costPerInterview: number;
  currency: string;
  publishDate: string;
  expiryDate: string;
  surveyLength: number;
  outlierCutoffTime: number;
  validateStartEndIp: string;
  validateCountry: string;
  blacklistSurvey: string[];
  surveyUrlIdentifier: string;
  colorCode: string;
  description_one: string;
  description_two: string;
  description_three: string;
  description_four: string;
  company: string;
  companyLogo: string;
  ipUnique: boolean;
  isActive: boolean;
  isPaused: boolean;
  // Pre-screening owned by parent
  preQuestionsYN: string;
  selectedPreScreenQues: any[];
  details: any[];
};
